<script setup lang="ts">
import { useLocalStorage } from '@vueuse/core'
import { computed } from 'vue'

import BoardFlipToggle from './BoardFlipToggle.vue'
import type { BellowsDirection } from './harmonika.types'
import HarmonikaButton from './HarmonikaButton.vue'
import { BOARDS_FLIPPED_STORAGE_KEY, PRECALC_DATA, withoutAmbiguousOctave } from './instrumentData'

type InstrumentVariant = 'inspector' | 'simulator'

const {
  variant = 'inspector',
  vertical = false,
  activeButtons,
  previewButtons = new Set<string>(),
  activeMidiNotes = new Set<number>(),
  getLabel = () => '',
  getKeyLabel = () => '',
  playable = true,
  direction = 'DRUCK',
} = defineProps<{
  variant?: InstrumentVariant
  /** Hochkant: Reihen als Spalten, mit Schalter „Darstellung umdrehen“ (gilt für alle Instanzen). */
  vertical?: boolean
  activeButtons: Set<string>
  previewButtons?: Set<string>
  activeMidiNotes?: Set<number>
  getLabel?: (rowIdx: number, btnIdx: number) => string
  getKeyLabel?: (rowIdx: number, btnIdx: number) => string
  /** Knöpfe klingen beim Drücken (`HarmonikaButton`); aus, wo der Aufrufer selbst spielt. */
  playable?: boolean
  /** Balgrichtung, deren Töne ein gedrückter Knopf spielt. */
  direction?: BellowsDirection
}>()

const emit = defineEmits<{
  activate: [rowIdx: number, btnIdx: number]
  deactivate: [rowIdx: number, btnIdx: number]
}>()

// Knöpfe samt Original-Index, auch umgekehrt vorberechnet: umgedreht wird in der DOM-Reihenfolge,
// damit die Tab-Reihenfolge dem Bild folgt.
const rowsLeftToRight = PRECALC_DATA.map((row) => {
  const buttons = row.buttons.map((buttonData, btnIdx) => ({ buttonData, btnIdx }))
  return { rowIdx: row.rowId - 1, buttons, buttonsReversed: [...buttons].reverse() }
})

const rowsTopToBottom = [...rowsLeftToRight].reverse()
const displayRows = computed(() => (vertical ? rowsLeftToRight : rowsTopToBottom))

const boardsFlipped = useLocalStorage(BOARDS_FLIPPED_STORAGE_KEY, false)
// Im Diskant sitzt Knopf 1 am Instrument oben – umgekehrt wird nur auf Wunsch.
const reversed = computed(() => vertical && boardsFlipped.value)

// Die ursprünglichen Reihenversätze bleiben erhalten. Die Abstände werden
// proportional zur Knopfgröße skaliert, auch in der Hochkantdarstellung.
const indentFactors: Record<InstrumentVariant, readonly number[]> = {
  inspector: [0.5, 1.125, 1.875, 2.625],
  simulator: [0.33, 0.75, 1.25, 1.75],
}

function rowStyle(rowIdx: number): Record<string, string> {
  const factor = indentFactors[variant][rowIdx] ?? indentFactors[variant][0] ?? 0.33

  return {
    '--row-indent': `calc(var(--button-size) * ${factor})`,
  }
}

function buttonId(rowIdx: number, btnIdx: number): string {
  return `${rowIdx}-${btnIdx}`
}

function activate(rowIdx: number, btnIdx: number): void {
  emit('activate', rowIdx, btnIdx)
}

function deactivate(rowIdx: number, btnIdx: number): void {
  emit('deactivate', rowIdx, btnIdx)
}
</script>

<template>
  <section
    class="treble-board"
    :class="[
      `treble-board--${variant}`,
      { 'treble-board--vertical': vertical },
    ]"
    aria-label="Diskantseite der Steirischen Harmonika"
  >
    <BoardFlipToggle v-if="vertical" v-model:flipped="boardsFlipped" />

    <div v-if="variant === 'simulator'" class="treble-board__hardware" aria-hidden="true">
      <span class="treble-board__fastener treble-board__fastener--top-left" />
      <span class="treble-board__fastener treble-board__fastener--top-right" />
      <span class="treble-board__fastener treble-board__fastener--bottom-left" />
      <span class="treble-board__fastener treble-board__fastener--bottom-right" />
      <span class="treble-board__serial">STUDIO / DISKANT</span>
    </div>

    <div class="treble-board__rows">
      <!-- Lage am Instrument. -->
      <span class="treble-board__end treble-board__end--before">{{ reversed ? 'unten' : 'oben' }}</span>
      <span class="treble-board__end treble-board__end--after">{{ reversed ? 'oben' : 'unten' }}</span>

      <div
        v-for="{ rowIdx, buttons, buttonsReversed } in displayRows"
        :key="rowIdx"
        class="treble-board__row"
        :style="rowStyle(rowIdx)"
      >
        <div class="treble-board__spacer" aria-hidden="true">
          <span class="treble-board__row-index">R{{ rowIdx + 1 }}</span>
        </div>

        <HarmonikaButton
          v-for="{ buttonData, btnIdx } in reversed ? buttonsReversed : buttons"
          :key="btnIdx"
          :variant="variant"
          :row-idx="rowIdx"
          :btn-idx="btnIdx"
          :btn-data="buttonData"
          :is-active="activeButtons.has(buttonId(rowIdx, btnIdx))"
          :is-preview="previewButtons.has(buttonId(rowIdx, btnIdx))"
          :label="withoutAmbiguousOctave(rowIdx, btnIdx, getLabel(rowIdx, btnIdx))"
          :key-label="getKeyLabel(rowIdx, btnIdx)"
          :active-midi-notes="activeMidiNotes"
          :playable="playable"
          :direction="direction"
          @activate="activate"
          @deactivate="deactivate"
        />

        <div class="treble-board__spacer" aria-hidden="true">
          <span class="treble-board__row-index">R{{ rowIdx + 1 }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Gemeinsame Geometrie; die Darstellung variiert ausschließlich über CSS. */
.treble-board {
  --button-gap: 0.42rem;
  --row-gap: 0.78rem;
  --index-color: #8b9aad;
  --guide-color: rgb(109 128 151 / 20%);

  position: relative;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: safe center;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-width: 0;
  padding: 1.65rem 1.5rem;
  overflow: auto;
  border: 1px solid #d8e0ea;
  border-radius: 1.5rem;
  background:
    radial-gradient(circle at 18% 0%, rgb(255 255 255 / 85%), transparent 60%),
    radial-gradient(circle, rgb(107 131 162 / 8%) 0.8px, transparent 1px) 0 0 / 18px 18px,
    linear-gradient(155deg, #f8fafc 5%, #ecf1f6 100%);
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 95%),
    inset 0 0 0 5px rgb(255 255 255 / 28%),
    0 12px 32px rgb(41 65 91 / 8%);
  overscroll-behavior: contain;
  scrollbar-color: #b1becd transparent;
  scrollbar-width: thin;
}

.treble-board::before {
  position: absolute;
  inset: 7px;
  border: 1px solid rgb(150 169 191 / 20%);
  border-radius: calc(1.5rem - 6px);
  content: '';
  pointer-events: none;
}

.treble-board::after {
  position: absolute;
  top: 11px;
  left: 50%;
  width: 80px;
  height: 2px;
  border-radius: 99px;
  background: linear-gradient(90deg, transparent, #67cacd 25% 75%, transparent);
  content: '';
  opacity: 0.65;
  pointer-events: none;
  transform: translateX(-50%);
}

/* Keine Größen-Containment-Regel im Hochkantmodus: w-fit muss messbar bleiben. */
.treble-board:not(.treble-board--vertical) {
  container-type: inline-size;
}

.treble-board--simulator {
  --button-gap: 0.52rem;
  --row-gap: 0.92rem;
  --index-color: #a8b7ca;
  --guide-color: rgb(183 202 224 / 16%);

  padding: 2.15rem 1.85rem;
  border: 2px solid #3c4a5f;
  border-radius: 1.8rem;
  background:
    radial-gradient(ellipse at 50% -30%, rgb(113 142 179 / 32%), transparent 65%),
    repeating-linear-gradient(90deg, rgb(255 255 255 / 1.6%) 0 1px, transparent 1px 7px),
    linear-gradient(155deg, #303c4d 0%, #202a39 55%, #171f2d 100%);
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 22%),
    inset 0 0 0 5px rgb(13 20 31 / 28%),
    inset 0 -16px 25px rgb(2 7 16 / 21%),
    0 18px 42px rgb(20 32 49 / 27%);
  scrollbar-color: #697a90 transparent;
}

.treble-board--simulator::before {
  inset: 9px;
  border-color: rgb(179 205 233 / 12%);
  border-radius: calc(1.8rem - 8px);
}

.treble-board--simulator::after {
  top: 12px;
  width: 140px;
  height: 3px;
  background: linear-gradient(90deg, transparent, #66e1d6 20% 80%, transparent);
  box-shadow: 0 0 18px rgb(102 225 214 / 35%);
}

.treble-board__rows {
  --button-size: 2.05rem;

  position: relative;
  z-index: 1;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--row-gap);
  width: max-content;
  margin: auto;
}

.treble-board--simulator:not(.treble-board--vertical) .treble-board__rows {
  /* Längste Reihe: 13 Knöpfe, zwei 0,33-Einrückungen, 14 Zwischenräume.
     Die Mindestgröße ermöglicht Scrollen bei schmalen Viewports. */
  --button-size: clamp(1.875rem, calc((100cqw - 11.1rem) / 13.66), 3rem);
}

.treble-board__row {
  position: relative;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: var(--button-gap);
  width: max-content;
  min-height: var(--button-size);
}

.treble-board__row::before {
  position: absolute;
  top: 50%;
  right: var(--row-indent);
  left: var(--row-indent);
  height: 1px;
  background: var(--guide-color);
  content: '';
  pointer-events: none;
}

.treble-board__spacer {
  display: flex;
  flex: 0 0 var(--row-indent);
  align-items: center;
  justify-content: center;
  align-self: stretch;
  min-width: 0;
}

.treble-board__row-index,
.treble-board__end {
  color: var(--index-color);
  font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
  font-size: 0.57rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.treble-board__row-index {
  letter-spacing: -0.02em;
  opacity: 0.86;
}

/* Liegt im Innenabstand des Korpus neben der Knopfreihe. */
.treble-board__end {
  position: absolute;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  pointer-events: none;
}

/* Waagrecht quer gestellt wie das Instrument. */
.treble-board:not(.treble-board--vertical) .treble-board__end {
  top: 50%;
  transform: translateY(-50%);
  writing-mode: vertical-rl;
}

.treble-board:not(.treble-board--vertical) .treble-board__end--before { right: calc(100% + 0.3rem); }
.treble-board:not(.treble-board--vertical) .treble-board__end--after { left: calc(100% + 0.3rem); }

.treble-board__hardware {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.treble-board__fastener {
  position: absolute;
  width: 7px;
  height: 7px;
  border: 1px solid #64748b;
  border-radius: 50%;
  background: linear-gradient(140deg, #9aaabf, #334155);
  box-shadow: 0 1px 2px rgb(0 0 0 / 45%);
}

.treble-board__fastener--top-left { top: 17px; left: 17px; }
.treble-board__fastener--top-right { top: 17px; right: 17px; }
.treble-board__fastener--bottom-left { bottom: 17px; left: 17px; }
.treble-board__fastener--bottom-right { right: 17px; bottom: 17px; }

.treble-board__serial {
  position: absolute;
  right: 36px;
  bottom: 13px;
  color: rgb(177 197 220 / 47%);
  font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
  font-size: 0.48rem;
  font-weight: 600;
  letter-spacing: 0.16em;
}

.treble-board--vertical {
  width: max-content;
  max-width: 100%;
  max-height: 100%;
}

.treble-board--vertical .treble-board__rows {
  flex-direction: row;
}

.treble-board--vertical.treble-board--simulator .treble-board__rows {
  --button-size: clamp(1.875rem, 3.4vw, 3rem);
}

.treble-board--vertical .treble-board__row {
  flex-direction: column;
  min-width: var(--button-size);
  min-height: 0;
}

.treble-board--vertical .treble-board__end {
  left: 50%;
  transform: translateX(-50%);
}

.treble-board--vertical .treble-board__end--before { bottom: calc(100% + 0.3rem); }
.treble-board--vertical .treble-board__end--after { top: calc(100% + 0.3rem); }

.treble-board--vertical .treble-board__row::before {
  top: var(--row-indent);
  right: auto;
  bottom: var(--row-indent);
  left: 50%;
  width: 1px;
  height: auto;
}

.treble-board--vertical .treble-board__spacer {
  flex-basis: var(--row-indent);
  align-self: center;
  width: var(--button-size);
}

.treble-board--vertical::after {
  top: 50%;
  left: 11px;
  width: 2px;
  height: 80px;
  background: linear-gradient(180deg, transparent, #67cacd 25% 75%, transparent);
  transform: translateY(-50%);
}

.treble-board--vertical.treble-board--simulator::after {
  left: 12px;
  width: 3px;
  height: 140px;
  background: linear-gradient(180deg, transparent, #66e1d6 20% 80%, transparent);
}

.treble-board--vertical .treble-board__serial {
  display: none;
}

@media (max-width: 640px) {
  .treble-board { padding: 1.2rem; border-radius: 1.15rem; }
  .treble-board--simulator { padding: 1.7rem 1.35rem; border-radius: 1.35rem; }
  .treble-board__row-index { font-size: 0.49rem; }
}
</style>
